'use client'

import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import Calendar from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/mobileCalendar'
import { IRange } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import { TranslationFunction } from '@/src/types/translations'
import { Holiday } from '@/src/constants/holidayCalendarConfig'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import { alpha, Box, Button, IconButton, Typography } from '@mui/material'
import React from 'react'

import HolidayCarouselWrapper from './holidayCarousel'

interface DateSelectorProps {
    isRtl: boolean
    open: boolean
    onOpen: () => void
    onClose: () => void
    isRoundTrip: boolean
    departureDate: string
    returnDate: string
    activeOnward: boolean
    activeReturn: boolean
    localRange: IRange
    returnTabEnabled: boolean
    onDepartureClick: () => void
    onReturnClick: () => void
    onDateClick: (dates: any) => void
    onApply: () => void
    directionClick: (departRange: any) => void
    t: TranslationFunction<'common'>
    minDate?: Date | null
    onSubmit: () => void
    fareCalendar?: Record<string, { amount: number; fareCategory: string }>
    cheapestPricesByMonth?: Record<string, number>
    formatCurrency?: (price: number, usePrecision?: boolean) => string
    holidays?: Holiday[]
}

const DateSelector: React.FC<DateSelectorProps> = ({
    isRtl,
    open,
    onOpen,
    onClose,
    isRoundTrip,
    departureDate,
    returnDate,
    activeOnward,
    activeReturn,
    localRange,
    returnTabEnabled,
    onDepartureClick,
    onReturnClick,
    onDateClick,
    onApply,
    directionClick,
    t,
    minDate,
    onSubmit,
    fareCalendar = {},
    cheapestPricesByMonth = {},
    formatCurrency,
    holidays = []
}) => {
    return (
        <SwipeableDrawerWithBackNavigation open={open} onOpen={onOpen} onClose={onClose} sx={{height:'100dvh',maxHeight:'100dvh'}}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent:'space-between',
                        alignItems: 'center',
                        py: 0.5
                    }}
                >
                 <IconButton
                    onClick={onClose}
                    sx={{
                        color: 'textColors.subheading'
                    }}
                    size="medium"
                >
                    <ArrowBackIosNewIcon sx={{ fontSize: 16 }} fontSize="inherit" />
                </IconButton>
                <Typography variant="body1" fontSize={18} fontWeight={600} ml={-3} mb={0.5}>
                    {t('selectDate')}
                </Typography>
                <Box />

                </Box>
                {/* Holiday Carousel */}
                {holidays && holidays.length > 0 && (
                    <HolidayCarouselWrapper
                        holidays={holidays}
                        isRoundTrip={isRoundTrip}
                        onDateClick={onDateClick}
                    />
                )}
                {/* Calendar */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        '& .DayPicker': {
                            width: '100%',
                            margin: 0
                        },
                        '& .DayPicker-wrapper': {
                            pb: 0,
                            outline: 'none'
                        },
                        '& .DayPicker-Month': {
                            width: '100%',
                            margin: 0,
                            display: 'table',
                            tableLayout: 'fixed'
                        },
                        '& .DayPicker-Body': {
                            display: 'table-row-group'
                        },
                        '& .DayPicker-Week': {
                            display: 'table-row'
                        },
                        '& .DayPicker-WeekdaysRow': {
                            display: 'table-row'
                        },
                        '& .DayPicker-Day': {
                            width: 'calc((100vw - 32px) / 7)',
                            height: 'calc((100vw - 32px) / 7)',
                            maxWidth: '50px',
                            maxHeight: '50px',
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            padding: 0,
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            WebkitTapHighlightColor: 'transparent',
                            touchAction: 'manipulation'
                        },
                        '& .DayPicker-Weekday': {
                            width: 'calc((100vw - 32px) / 7)',
                            maxWidth: '50px',
                            display: 'table-cell',
                            textAlign: 'center',
                            padding: '8px 0',
                            color: '#9e9e9e',
                            fontSize: '14px'
                        },
                        '& .DayPicker-Caption': {
                            display: 'table-caption',
                            marginBottom: '16px',
                            textAlign: 'center'
                        },
                        '& .DayPicker-NavBar': {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0
                        }
                    }}
                >
                    <Calendar
                        selectedDates={localRange}
                        directionClick={directionClick}
                        roundTrip={isRoundTrip}
                        onClickCalendar={onDateClick}
                        activeOnward={activeOnward}
                        activeReturn={activeReturn}
                        numberOfMonths={1}
                        isRtl={isRtl}
                        isMulticity={false}
                        multiCityDestinations={{}}
                        index={1}
                        minDate={minDate}
                        fareCalendar={fareCalendar}
                        cheapestPricesByMonth={cheapestPricesByMonth}
                        formatCurrency={formatCurrency}
                    />
                </Box>
                 {/* Date Selection Tabs for Round Trip */}
                 {isRoundTrip && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Box sx={{display:'flex', gap: 2,mb: 2}}>
                            <Box flex={1}>
                                <Typography variant="body2" fontWeight={600} mb={0.5}>
                                    {t('departure')}
                                </Typography>
                                <Box
                                    onClick={onDepartureClick}
                                    sx={theme => ({
                                        flex: 1,
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: '2px solid',
                                        borderColor: activeOnward ? 'primary.main' : 'divider',
                                        bgcolor: activeOnward ? alpha(theme.palette.primary.light, 0.2) : 'transparent',
                                        cursor: 'pointer'
                                    })}
                                >
                                    <Typography variant="body2" fontWeight={600}>
                                        {departureDate}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box flex={1}>
                                <Typography variant="body2" fontWeight={600} mb={0.5}>
                                    {t('return')}
                                </Typography>
                                <Box
                                    onClick={onReturnClick}
                                    sx={theme => ({
                                        flex: 1,
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: '2px solid',
                                        borderColor: activeReturn ? 'primary.main' : 'divider',
                                        bgcolor: activeReturn ? alpha(theme.palette.primary.light, 0.2) : 'transparent',
                                        cursor: returnTabEnabled ? 'pointer' : 'not-allowed',
                                        opacity: returnTabEnabled ? 1 : 0.5
                                    })}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        {returnDate}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{display:'flex', gap: 2,mb: 1}}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    onClick={onSubmit}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        bgcolor: theme => theme.palette.primary.dark
                                    }}
                                >
                                    {t('done')}
                                </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </SwipeableDrawerWithBackNavigation>
    )
}

export { DateSelector }
export type { DateSelectorProps }
